import React, { useEffect, useMemo, useState } from 'react';
import { X, ExternalLink, Filter, Table, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { reportsApi } from '../../../reports/api/reportsApi';
import { useObject } from '../../../analytics-shared/hooks/useFieldCatalog';
import { formatValue } from '../../../analytics-shared/engine/reportEngine';

/**
 * Drill-down modal opened when a user clicks a chart slice on a dashboard
 * widget. Previously this read from the fixture record set — which meant
 * clicking into a chart of real data took you to a list of synthetic
 * deals. Now it fetches live records via useRecords and live schema via
 * useObject, applies the source report's filters, and then filters down
 * to the clicked category using the WIDGET's xField (since the widget,
 * not the report's embedded chart, owns the chart configuration).
 */
const DrillDownModal = ({ drillDown, onClose }) => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  // After wedge 5 we no longer pull records directly + filter them
  // client-side. Instead we run the underlying report through the server
  // engine (POST /reports/:id/run) and then narrow the already-filtered
  // record list down to the clicked bucket — a single key-value lookup,
  // not a filter-tree execution.
  const [runResult, setRunResult] = useState(null);
  const [runLoading, setRunLoading] = useState(false);

  useEffect(() => {
    if (!drillDown?.widget?.reportId) { setReport(null); return; }
    reportsApi.get(drillDown.widget.reportId).then(setReport);
  }, [drillDown]);

  useEffect(() => {
    if (!report?.id) { setRunResult(null); return undefined; }
    let cancelled = false;
    setRunLoading(true);
    reportsApi.run(report)
      .then((res) => { if (!cancelled) setRunResult(res); })
      .catch(() => { if (!cancelled) setRunResult({ records: [] }); })
      .finally(() => { if (!cancelled) setRunLoading(false); });
    return () => { cancelled = true; };
  }, [report]);

  const { data: object } = useObject(report?.object);
  const recordsLoading = runLoading;

  const fieldMap = useMemo(() => {
    if (!object?.fields) return {};
    return object.fields.reduce((acc, f) => { acc[f.key] = f; return acc; }, {});
  }, [object]);

  // Widget config takes precedence; fall back to the report's first
  // embedded chart for legacy widgets that haven't been re-saved yet.
  const groupField = drillDown?.widget?.config?.xField
    || report?.charts?.[drillDown?.widget?.chartIndex ?? 0]?.groupField
    || null;

  const drillRecords = useMemo(() => {
    if (!groupField || !report || !runResult) return [];
    // runResult.records is already filtered by the report's saved
    // filters (server applied them); the only remaining narrowing is
    // the bucket lookup, which is a single field comparison.
    const cat = String(drillDown?.category ?? '').toLowerCase();
    return (runResult.records || [])
      .filter((r) => String(r[groupField] ?? '').toLowerCase() === cat);
  }, [groupField, report, runResult, drillDown?.category]);

  if (!drillDown || !report) return null;

  // Column order: explicit report columns → widget table columns → first
  // 7 fields off the object schema. Keeps the drill-down feeling like the
  // source report rather than a generic list view.
  const displayCols = (
    (Array.isArray(report.columns) && report.columns.length ? report.columns : null)
    || (Array.isArray(drillDown.widget?.config?.tableColumns) && drillDown.widget.config.tableColumns.length
      ? drillDown.widget.config.tableColumns : null)
    || (object?.fields || []).map((f) => f.key)
  ).slice(0, 7);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[82vh] flex flex-col">
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 bg-indigo-100 rounded-lg">
                <Filter className="w-3.5 h-3.5 text-indigo-600" />
              </div>
              <h3 className="text-base font-bold text-slate-800">Drill Down</h3>
            </div>
            <p className="text-sm text-slate-500 ml-9">
              Showing <span className="font-semibold text-slate-700">{drillRecords.length}</span> records where{' '}
              <span className="font-semibold text-indigo-600">{(fieldMap[groupField] || {}).label || groupField}</span> = &ldquo;
              <span className="font-semibold text-slate-700">{drillDown.category}</span>&rdquo;
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
            <button onClick={() => { navigate(`/reports/view/${report.id}`); onClose(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors">
              <ExternalLink className="w-3.5 h-3.5" /> Open Full Report
            </button>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <X className="w-4 h-4 text-slate-600" />
            </button>
          </div>
        </div>

        <div className="px-6 py-2 bg-slate-50 border-b border-slate-100 flex-shrink-0 flex items-center gap-2">
          <Table className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs text-slate-500">
            Source: <span className="font-medium text-slate-700">{report.name}</span>
            {' · '}<span className="capitalize">{report.object}</span>
          </span>
        </div>

        <div className="flex-1 overflow-auto">
          {recordsLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
              <p className="text-xs">Loading records…</p>
            </div>
          ) : drillRecords.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <Filter className="w-10 h-10 text-slate-200 mb-3" />
              <p className="text-sm font-medium text-slate-500">No records match this filter</p>
              <p className="text-xs mt-1">Try a different segment</p>
            </div>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-10">
                <tr className="bg-slate-50 border-b border-slate-200">
                  {displayCols.map((col) => (
                    <th key={col} className="text-left px-4 py-2.5 text-slate-500 font-semibold text-[11px] uppercase tracking-wide whitespace-nowrap">
                      {fieldMap[col]?.label || col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {drillRecords.slice(0, 200).map((rec, i) => (
                  <tr key={rec.id || i} className="border-b border-slate-100 hover:bg-indigo-50/30 transition-colors">
                    {displayCols.map((col) => (
                      <td key={col} className="px-4 py-2 text-slate-700 whitespace-nowrap max-w-[180px] truncate">
                        {formatValue(rec[col], fieldMap[col])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {drillRecords.length > 200 && (
          <div className="px-6 py-2.5 bg-slate-50 border-t border-slate-200 text-xs text-slate-500 flex-shrink-0">
            Showing first 200 of {drillRecords.length} records.{' '}
            <button className="text-indigo-600 hover:underline" onClick={() => { navigate(`/reports/view/${report.id}`); onClose(); }}>
              Open full report
            </button>{' '}
            to see all.
          </div>
        )}
      </div>
    </div>
  );
};

export default DrillDownModal;
